<script setup lang="ts">
import HealthcenterGroupAvailabilityMapMarker
    from "@/disruption-logistics/components/maps/healthcenter-groups/HealthcenterGroupAvailabilityMapMarker.vue";
import {InfoWindow} from "vue3-google-map";
import HealthcenterGroupAvailabilityInfoWindow
    from "@/disruption-logistics/components/maps/healthcenter-groups/HealthcenterGroupAvailabilityInfoWindow.vue";
import DisruptionLogisticsMap from "@/disruption-logistics/components/maps/DisruptionLogisticsMap.vue";
import {HealthcenterGroupMember} from "@/disruption-logistics/interfaces/availability-providers";
import {HealthcenterGroup} from "@/disruption-logistics/interfaces/healthcenter-groups";
import {nextTick, ref, watch} from "vue";

const props = defineProps<{
    loading: boolean,
    disabled?: boolean,
    infoContent?: {
        healthcenter: HealthcenterGroupMember,
        groups: HealthcenterGroup[],
        position: { lat: number, lng: number }
    },
    open: boolean,
    groupingGroup?: HealthcenterGroup,
    uniqueHealthCenters: HealthcenterGroupMember[]
}>();
defineEmits<{ (e: 'toggle-info-window', healthcenter: HealthcenterGroupMember): void }>();

const infoWindowOpened = ref<boolean>(props.open);
const mapRef = ref<InstanceType<typeof DisruptionLogisticsMap>>();

/**
 * France metropolitan bounds
 */
const FRANCE_BOUNDS = [
    // North-West (Brittany)
    {lat: 51.089166, lng: -5.142222},

    {lat: 42.333333, lng: 9.560000}
];

/**
 * Fit the map to display all group members.
 * If no healthcenters with valid coordinates, display France bounds.
 */
const fitMapToGroups = () => {
    nextTick(() => {
        if (mapRef.value) {
            // Display France bounds when no healthcenters
            mapRef.value.fitMapToBounds(FRANCE_BOUNDS);
        }
    });
};

/**
 * Tell if the healthcenter is part of grouping group.
 */
const isGroupingGroup = (healthcenter: HealthcenterGroupMember): boolean => {
    if (!props.groupingGroup) {
        return false;
    }

    return props.groupingGroup.healthcenters.find(hc => hc.id === healthcenter.id) !== undefined;
};

watch(() => props.infoContent, () => {
    if (props.disabled) {
        infoWindowOpened.value = false;
    } else {
        infoWindowOpened.value = props.open;
    }

    if (!infoWindowOpened.value) {
        fitMapToGroups();
    }
}, {immediate: true, deep: true});

defineExpose({fitMapToGroups});
</script>

<template>
  <disruption-logistics-map
    ref="mapRef"
    :loading-data="loading"
    :disabled="disabled"
  >
    <info-window
      v-if="infoContent"
      v-model="infoWindowOpened"
      :options="{
              position: {
                lat: infoContent?.position.lat as number,
                lng: infoContent?.position.lng as number
              }}"
    >
      <healthcenter-group-availability-info-window :content="infoContent" />
    </info-window>
    <healthcenter-group-availability-map-marker
      v-for="healthcenter in uniqueHealthCenters"
      :key="healthcenter.id"
      :healthcenter="healthcenter"
      :is-grouping="isGroupingGroup(healthcenter)"
      :disabled="disabled"
      @click="$emit('toggle-info-window', healthcenter)"
    />
  </disruption-logistics-map>
</template>

<style scoped>

</style>